import { CSSProperties, RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

export type TAnchoredPlacement = 'bottom' | 'top';

export interface IUseAnchoredFixedPositionOptions {
  isEnabled: boolean;
  preferredPlacement?: TAnchoredPlacement;
  estimatedPanelHeight?: number;
  offset?: number;
  viewportPadding?: number;
}

export interface IUseAnchoredFixedPositionResult {
  anchorRef: RefObject<HTMLDivElement | null>;
  style: CSSProperties;
  placement: TAnchoredPlacement;
  availableSpace: number;
  isMeasured: boolean;
}

interface IAnchorMeasurement {
  top: number;
  bottom: number;
  left: number;
  width: number;
  viewportHeight: number;
}

const areMeasurementsEqual = (a: IAnchorMeasurement | null, b: IAnchorMeasurement | null): boolean => {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.viewportHeight === b.viewportHeight
  );
};

export const useAnchoredFixedPosition = (
  options: IUseAnchoredFixedPositionOptions
): IUseAnchoredFixedPositionResult => {
  const {
    isEnabled,
    preferredPlacement = 'bottom',
    estimatedPanelHeight = 0,
    offset = -1,
    viewportPadding = 8,
  } = options;

  const anchorRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [measurement, setMeasurement] = useState<IAnchorMeasurement | null>(null);

  const measure = useCallback(() => {
    const anchorElement = anchorRef.current;
    if (!anchorElement) {
      return;
    }

    const rect = anchorElement.getBoundingClientRect();
    const nextMeasurement: IAnchorMeasurement = {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      viewportHeight: window.innerHeight,
    };

    setMeasurement(previous => (areMeasurementsEqual(previous, nextMeasurement) ? previous : nextMeasurement));
  }, []);

  useLayoutEffect(() => {
    if (!isEnabled) {
      setMeasurement(null);
      return;
    }

    const scheduleMeasure = () => {
      if (animationFrameRef.current !== null) {
        return;
      }
      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = null;
        measure();
      });
    };

    measure();

    window.addEventListener('scroll', scheduleMeasure, true);
    window.addEventListener('resize', scheduleMeasure);

    let resizeObserver: ResizeObserver | null = null;
    if (anchorRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(anchorRef.current);
    }

    return () => {
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
      resizeObserver?.disconnect();
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isEnabled, measure]);

  if (measurement === null) {
    return {
      anchorRef,
      style: isEnabled ? { position: 'fixed', visibility: 'hidden' } : {},
      placement: preferredPlacement,
      availableSpace: 0,
      isMeasured: false,
    };
  }

  const spaceBelow = measurement.viewportHeight - measurement.bottom - viewportPadding;
  const spaceAbove = measurement.top - viewportPadding;

  const preferredSpace = preferredPlacement === 'bottom' ? spaceBelow : spaceAbove;
  const oppositeSpace = preferredPlacement === 'bottom' ? spaceAbove : spaceBelow;

  const shouldFlip = preferredSpace < estimatedPanelHeight && oppositeSpace > preferredSpace;
  const placement: TAnchoredPlacement = shouldFlip
    ? preferredPlacement === 'bottom'
      ? 'top'
      : 'bottom'
    : preferredPlacement;

  const availableSpace = placement === 'bottom' ? spaceBelow : spaceAbove;

  const style: CSSProperties = {
    position: 'fixed',
    left: `${measurement.left}px`,
    width: `${measurement.width}px`,
  };

  if (placement === 'bottom') {
    style.top = `${measurement.bottom + offset}px`;
  } else {
    style.bottom = `${measurement.viewportHeight - measurement.top - offset}px`;
  }

  return {
    anchorRef,
    style,
    placement,
    availableSpace,
    isMeasured: true,
  };
};
